import {parseISO} from 'date-fns';

export type DateInput = Date | string | number;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const parseDateInput = (value: DateInput): Date =>
  value instanceof Date
    ? value
    : typeof value === 'string' && DATE_ONLY_RE.test(value)
      ? parseISO(`${value}T00:00:00`)
      : typeof value === 'string'
        ? parseISO(value)
        : new Date(value);

export const parseUtcDateInput = (value: DateInput): Date => {
  if (typeof value !== 'string' || DATE_ONLY_RE.test(value)) {
    return parseDateInput(value);
  }
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return parseDateInput(hasTimeZone ? value : `${value}Z`);
};

/**
 * A calendar date with an explicit year. Date-only ISO strings are interpreted
 * as local calendar dates so a value like 2026-09-11 never renders as Sep 10
 * for viewers west of UTC.
 */
export const formatDateWithYear = (value: DateInput | null | undefined): string => {
  if (value == null || value === '') {
    return '';
  }
  const date = parseDateInput(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Formats a UTC timestamp as a local calendar date with a year. Legacy API
 * timestamps without an offset are interpreted as UTC; date-only values keep
 * their calendar date.
 */
export const formatUtcDateWithYear = (value: DateInput | null | undefined): string => {
  if (value == null || value === '') {
    return '';
  }
  return formatDateWithYear(parseUtcDateInput(value));
};

/**
 * The app's standard short date: "Jun 15", dropping the year when it matches the
 * current year and appending it ("Jun 15, 2024") otherwise. Accepts a Date, an
 * ISO string, or an epoch; returns '' for nullish/empty/invalid input.
 */
export const formatShortDate = (value: DateInput | null | undefined): string => {
  if (value == null || value === '') {
    return '';
  }
  const date = parseDateInput(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : {year: 'numeric'}),
  });
};

export const formatUtcShortDate = (value: DateInput | null | undefined): string => {
  if (value == null || value === '') {
    return '';
  }
  return formatShortDate(parseUtcDateInput(value));
};

/**
 * A span of days as a compact age: "3d" under a week, "2w" under 60 days, else
 * "3mo". The caller adds the words ("3d ago", "2w overdue").
 */
export const formatDayAge = (days: number): string =>
  days < 7 ? `${days}d` : days < 60 ? `${Math.floor(days / 7)}w` : `${Math.floor(days / 30)}mo`;

/**
 * Short date range built on {@link formatShortDate}. Collapses an identical
 * start/end to a single date, and drops the redundant year from the start when
 * both endpoints share a year (e.g. "Jun 15 – Jun 18, 2024"). Either endpoint
 * may be missing.
 */
export const formatShortDateRange = (
  start: DateInput | null | undefined,
  end: DateInput | null | undefined
): string => {
  const startText = formatShortDate(start);
  const endText = formatShortDate(end);

  if (!startText && !endText) return '';
  if (!startText) return endText;
  if (!endText) return startText;
  if (startText === endText) return startText;

  const startDate = parseDateInput(start as DateInput);
  const endDate = parseDateInput(end as DateInput);
  const sharesYear =
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime()) &&
    startDate.getFullYear() === endDate.getFullYear();

  const startLabel = sharesYear
    ? startDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
    : startText;

  return `${startLabel} – ${endText}`;
};
